import { recordRouteFamilyMetric } from "./routeFamilyMonitoring";

const MONITORING_EVENT = "ecotour:performance-monitoring";
const PORTAL_TELEMETRY_EVENT = "ecotour:telemetry";
const DEFAULT_BATCH_SIZE = 15;
const DEFAULT_FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE_SIZE = 60;
const MAX_RETRY_COUNT = 2;
const MAX_IMAGE_EVENTS_PER_PAGE_VIEW = 80;
const MAX_IMAGE_FALLBACK_EVENTS_PER_PAGE_VIEW = 30;
const MAX_IMAGE_FAILURE_EVENTS_PER_PAGE_VIEW = 30;
const SENSITIVE_ROUTE_PREDECESSORS = new Set([
  "invite",
  "reset-password",
  "share",
  "token",
  "content",
]);
const ALLOWED_VITALS = new Set(["cls", "fcp", "fid", "inp", "lcp", "ttfb"]);
const IMAGE_EVENT_OPERATIONS = Object.freeze({
  image_delivery_work_started: { operation: "work_started", metric: "image_queue" },
  image_delivery_loaded: { operation: "loaded", metric: "image_load" },
  image_delivery_source_error: { operation: "source_error", metric: "image_fallback" },
  image_delivery_failed: { operation: "failed", metric: "image_failure" },
  image_delivery_contract_missing: { operation: "contract_missing", metric: "missing_variant" },
  image_delivery_stuck: { operation: "stuck", metric: "image_stuck" },
});
const LANDING_OPERATIONS = new Set([
  "route_cache", "critical_ready", "deferred_ready", "critical_error", "deferred_error",
]);

const normalizeBoolean = (value, fallback) => {
  if (value === undefined || value === null || value === "") return fallback;
  return String(value).trim().toLowerCase() === "true";
};

export const normalizeSampleRate = (value, fallback = 1) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(1, Math.max(0, parsed));
};

const normalizeNumber = (value, { min = 0, max = 3_600_000, decimals = 3 } = {}) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Number(Math.min(max, Math.max(min, parsed)).toFixed(decimals));
};

const safeOperationalText = (value, maxLength = 80) => String(value || "")
  .replace(/[^a-z0-9._~-]/gi, "-")
  .slice(0, maxLength);

const createPageViewId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `rum_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

const isSensitiveSegment = (segment = "") =>
  /^[a-f0-9]{24}$/i.test(segment)
  || /^[0-9]{6,}$/.test(segment)
  || /^[a-f0-9-]{32,}$/i.test(segment)
  || /^(?:RBK|SBK|CBK|TBK|PBK|MBRQ)-?[a-z0-9-]{6,}$/i.test(segment)
  || segment.includes("@");

export const sanitizeMonitoringRoute = (value = "/") => {
  let pathname = String(value || "/").split(/[?#]/, 1)[0] || "/";
  try {
    if (/^https?:\/\//i.test(pathname)) pathname = new URL(pathname).pathname;
  } catch {
    pathname = "/";
  }
  const segments = pathname
    .split("/")
    .filter(Boolean)
    .slice(0, 10);
  const normalized = segments.map((segment, index) => {
    const previous = String(segments[index - 1] || "").toLowerCase();
    if (SENSITIVE_ROUTE_PREDECESSORS.has(previous) || isSensitiveSegment(segment)) return ":id";
    return segment.replace(/[^a-z0-9._~-]/gi, "-").slice(0, 48) || ":segment";
  });
  return `/${normalized.join("/")}`.slice(0, 180) || "/";
};

export const sanitizeApiEndpoint = (value = "/") =>
  sanitizeMonitoringRoute(String(value || "/").replace(/^https?:\/\/[^/]+/i, ""));

export const sanitizeErrorForMonitoring = (value) => {
  const error = value instanceof Error ? value : null;
  const name = String(error?.name || value?.name || "Error")
    .replace(/[^a-z0-9_$.-]/gi, "")
    .slice(0, 60) || "Error";
  const message = String(error?.message || value?.message || value || "Runtime error")
    .replace(/https?:\/\/\S+/gi, "[url]")
    .replace(/[\w.+-]+@[\w.-]+\.[a-z]{2,}/gi, "[email]")
    .replace(/(bearer|token|secret|password)\s*[:=]?\s*[^\s,;]+/gi, "$1=[redacted]")
    .replace(/[?#][^\s]+/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 180);
  return { name, message };
};

const resolveViewportClass = () => {
  if (typeof window === "undefined") return "unknown";
  if (window.matchMedia?.("(max-width: 767px)").matches) return "mobile";
  if (window.matchMedia?.("(max-width: 1023px)").matches) return "tablet";
  return "desktop";
};

const resolveConnection = () => {
  if (typeof navigator === "undefined") return { effectiveType: "", saveData: false };
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  return {
    effectiveType: String(connection?.effectiveType || "").slice(0, 12),
    saveData: Boolean(connection?.saveData),
  };
};

export const resolvePerformanceMonitoringConfig = () => {
  const apiBase = String(process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");
  const endpoint = String(
    process.env.REACT_APP_PERFORMANCE_MONITORING_ENDPOINT
      || (apiBase ? `${apiBase}/v1/performance/events` : ""),
  ).trim();
  const held = normalizeBoolean(
    process.env.REACT_APP_PERFORMANCE_TELEMETRY_HOLD,
    true,
  );
  return {
    enabled: normalizeBoolean(
      process.env.REACT_APP_PERFORMANCE_MONITORING_ENABLED,
      false,
    ) && !held && Boolean(endpoint),
    held,
    endpoint,
    environment: String(process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || "unknown")
      .trim().toLowerCase().slice(0, 32),
    release: String(
      process.env.REACT_APP_RELEASE_SHA
        || process.env.REACT_APP_BUILD_ID
        || "unknown",
    ).trim().slice(0, 64),
    sampleRate: normalizeSampleRate(process.env.REACT_APP_PERFORMANCE_MONITORING_SAMPLE_RATE, 0.05),
  };
};

let config = resolvePerformanceMonitoringConfig();
let initialized = false;
let sampled = false;
let pageViewId = createPageViewId();
let currentRoute = "/";
let queue = [];
let flushTimer = null;
let flushPromise = null;
let navigationTimingRecorded = false;
let longTaskObserver = null;
let longTaskSummary = { count: 0, totalDuration: 0, maxDuration: 0 };
let imageEventCounts = { regular: 0, fallback: 0, failure: 0 };

const updateDebugState = (status, event = "") => {
  if (typeof document !== "undefined") {
    const root = document.documentElement;
    root.dataset.performanceMonitoring = config.enabled ? "active" : "disabled";
    root.dataset.performanceMonitoringStatus = status;
    root.dataset.performanceMonitoringLastEvent = event;
    root.dataset.performanceMonitoringQueueSize = String(queue.length);
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(MONITORING_EVENT, {
      detail: { status, event, queueSize: queue.length },
    }));
  }
};

const scheduleFlush = () => {
  if (flushTimer || typeof window === "undefined") return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    flushPerformanceMonitoring().catch(() => null);
  }, DEFAULT_FLUSH_INTERVAL_MS);
};

const buildBaseEvent = (event, data = {}) => ({
  event,
  occurredAt: new Date().toISOString(),
  route: currentRoute,
  pageViewId,
  release: config.release,
  environment: config.environment,
  viewport: resolveViewportClass(),
  connection: resolveConnection(),
  data,
});

const enqueue = (event, data = {}) => {
  if (!config.enabled || !sampled) return false;
  if (queue.length >= MAX_QUEUE_SIZE) queue.shift();
  queue.push({ payload: buildBaseEvent(event, data), retries: 0 });
  updateDebugState("queued", event);
  if (queue.length >= DEFAULT_BATCH_SIZE) flushPerformanceMonitoring().catch(() => null);
  else scheduleFlush();
  return true;
};

const deliverBatch = async (entries, { beacon = false } = {}) => {
  const body = JSON.stringify({ schemaVersion: 1, events: entries.map((entry) => entry.payload) });
  if (beacon && typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    const accepted = navigator.sendBeacon(config.endpoint, body);
    if (accepted) return { accepted: true, status: 202 };
  }
  const response = await fetch(config.endpoint, {
    method: "POST",
    body,
    credentials: "omit",
    keepalive: true,
  });
  if (!response.ok) throw new Error(`Performance monitoring rejected with ${response.status}`);
  return { accepted: true, status: response.status };
};

export const flushPerformanceMonitoring = async ({ beacon = false } = {}) => {
  if (!config.enabled || !queue.length) return false;
  if (flushPromise) return flushPromise;
  if (flushTimer && typeof window !== "undefined") {
    window.clearTimeout(flushTimer);
    flushTimer = null;
  }
  const entries = queue.splice(0, DEFAULT_BATCH_SIZE);
  flushPromise = deliverBatch(entries, { beacon })
    .then(({ status }) => {
      if (typeof document !== "undefined") {
        const root = document.documentElement;
        const sent = Number(root.dataset.performanceMonitoringSent || 0) + entries.length;
        root.dataset.performanceMonitoringSent = String(sent);
        root.dataset.performanceMonitoringLastStatus = String(status);
      }
      updateDebugState("sent", entries[entries.length - 1]?.payload?.event || "");
      return true;
    })
    .catch(() => {
      const retryable = entries
        .map((entry) => ({ ...entry, retries: entry.retries + 1 }))
        .filter((entry) => entry.retries <= MAX_RETRY_COUNT);
      queue = [...retryable, ...queue].slice(0, MAX_QUEUE_SIZE);
      updateDebugState("retrying", entries[0]?.payload?.event || "");
      scheduleFlush();
      return false;
    })
    .finally(() => {
      flushPromise = null;
      if (queue.length) scheduleFlush();
    });
  return flushPromise;
};

export const recordPerformanceWebVital = (metric = {}) => {
  const name = String(metric.name || "").trim().toLowerCase();
  if (!ALLOWED_VITALS.has(name)) return false;
  return enqueue("web_vital", {
    metric: name,
    value: normalizeNumber(metric.value),
    delta: normalizeNumber(metric.delta),
    rating: ["good", "needs-improvement", "poor"].includes(metric.rating) ? metric.rating : "",
  });
};

export const recordPerformanceLandingExperience = (operation, data = {}) => {
  const normalizedOperation = String(operation || "").trim().toLowerCase();
  if (!LANDING_OPERATIONS.has(normalizedOperation)) return false;
  const isError = normalizedOperation.endsWith("_error");
  const isCache = normalizedOperation === "route_cache";
  const accepted = enqueue("landing_experience", {
    operation: normalizedOperation,
    metric: isCache ? "route_cache" : isError ? "landing_error" : "landing_ready",
    value: normalizeNumber(isCache ? data.cacheAgeMs : data.duration),
    duration: normalizeNumber(data.duration),
    segment: safeOperationalText(data.segment, 20),
    cacheStatus: ["hit", "miss", "stale"].includes(data.cacheStatus)
      ? data.cacheStatus
      : "",
  });
  const routeOperation = {
    critical_ready: "compact_ready",
    critical_error: "compact_failed",
    deferred_ready: "deferred_ready",
    deferred_error: "deferred_failed",
    route_cache: "cache_status",
  }[normalizedOperation];
  if (routeOperation) {
    recordRouteFamilyMetric(routeOperation, {
      duration: data.duration,
      segment: data.segment,
      cacheStatus: data.cacheStatus,
      inventoryState: isError ? "failed" : "ready",
      retained: data.cacheStatus === "hit" || data.cacheStatus === "stale",
    });
  }
  return accepted;
};

const recordNavigationTiming = () => {
  if (navigationTimingRecorded || typeof performance === "undefined") return;
  const navigation = performance.getEntriesByType?.("navigation")?.[0];
  if (!navigation) return;
  navigationTimingRecorded = true;
  enqueue("navigation_timing", {
    type: String(navigation.type || "navigate").slice(0, 20),
    dns: normalizeNumber(navigation.domainLookupEnd - navigation.domainLookupStart),
    connect: normalizeNumber(navigation.connectEnd - navigation.connectStart),
    tls: navigation.secureConnectionStart > 0
      ? normalizeNumber(navigation.connectEnd - navigation.secureConnectionStart)
      : 0,
    request: normalizeNumber(navigation.responseStart - navigation.requestStart),
    response: normalizeNumber(navigation.responseEnd - navigation.responseStart),
    domInteractive: normalizeNumber(navigation.domInteractive),
    load: normalizeNumber(navigation.loadEventEnd || navigation.duration),
    transferSize: normalizeNumber(navigation.transferSize, { max: 100_000_000, decimals: 0 }),
    encodedBodySize: normalizeNumber(navigation.encodedBodySize, { max: 100_000_000, decimals: 0 }),
  });
};

const flushLongTaskSummary = () => {
  if (!longTaskSummary.count) return;
  enqueue("long_task_summary", {
    count: longTaskSummary.count,
    totalDuration: normalizeNumber(longTaskSummary.totalDuration),
    maxDuration: normalizeNumber(longTaskSummary.maxDuration),
  });
  longTaskSummary = { count: 0, totalDuration: 0, maxDuration: 0 };
};

export const recordPerformanceNavigation = (value = "/") => {
  const nextRoute = sanitizeMonitoringRoute(value);
  if (currentRoute !== nextRoute) flushLongTaskSummary();
  currentRoute = nextRoute;
  pageViewId = createPageViewId();
  imageEventCounts = { regular: 0, fallback: 0, failure: 0 };
  enqueue("navigation", {
    navigationType: navigationTimingRecorded ? "spa" : "document",
  });
  if (typeof window !== "undefined") window.setTimeout(recordNavigationTiming, 0);
};

const handlePortalTelemetry = (event) => {
  const detail = event?.detail || {};
  if (detail.event === "api_request") {
    enqueue("api_request", {
      endpoint: sanitizeApiEndpoint(detail.endpoint),
      method: String(detail.method || "GET").toUpperCase().slice(0, 8),
      status: normalizeNumber(detail.status, { max: 599, decimals: 0 }),
      duration: normalizeNumber(detail.duration),
      outcome: [
        "ok",
        "http_error",
        "network_error",
        "timeout",
        "cancelled_by_navigation",
        "cancelled_by_component",
      ].includes(detail.outcome)
        ? detail.outcome
        : "network_error",
      cacheStatus: safeOperationalText(detail.cacheStatus, 24),
      payloadBytes: normalizeNumber(detail.payloadBytes, { max: 100_000_000, decimals: 0 }),
      negative: Boolean(detail.negative),
    });
    return;
  }

  if (detail.event === "route_family") {
    const operation = String(detail.operation || "").trim().toLowerCase();
    const allowedOperations = new Set([
      "route_enter", "cache_status", "compact_ready", "compact_empty", "compact_failed",
      "deferred_ready", "deferred_empty", "deferred_failed", "route_return",
    ]);
    if (!allowedOperations.has(operation)) return;
    enqueue("route_family", {
      operation,
      metric: safeOperationalText(detail.metric, 24),
      value: normalizeNumber(detail.value ?? detail.duration),
      duration: normalizeNumber(detail.duration),
      routeFamily: safeOperationalText(detail.routeFamily, 40),
      routeKind: safeOperationalText(detail.routeKind, 20),
      segment: safeOperationalText(detail.segment, 40),
      cacheStatus: safeOperationalText(detail.cacheStatus, 24),
      inventoryState: safeOperationalText(detail.inventoryState, 20),
      itemCount: normalizeNumber(detail.itemCount, { max: 100_000, decimals: 0 }),
      payloadBytes: normalizeNumber(detail.payloadBytes, { max: 100_000_000, decimals: 0 }),
      retained: Boolean(detail.retained),
    });
    return;
  }

  const imageMapping = IMAGE_EVENT_OPERATIONS[detail.event];
  if (!imageMapping) return;
  const imageEventBucket = ["failed", "stuck"].includes(imageMapping.operation)
    ? "failure"
    : imageMapping.operation === "source_error"
      ? "fallback"
      : "regular";
  const imageEventLimit = imageEventBucket === "failure"
    ? MAX_IMAGE_FAILURE_EVENTS_PER_PAGE_VIEW
    : imageEventBucket === "fallback"
      ? MAX_IMAGE_FALLBACK_EVENTS_PER_PAGE_VIEW
      : MAX_IMAGE_EVENTS_PER_PAGE_VIEW;
  if (imageEventCounts[imageEventBucket] >= imageEventLimit) return;
  const duration = imageMapping.operation === "loaded"
    ? detail.controlledWorkMs
    : imageMapping.operation === "work_started"
      ? detail.queueWaitMs
      : 0;
  const accepted = enqueue("image_delivery", {
    operation: imageMapping.operation,
    metric: imageMapping.metric,
    value: normalizeNumber(duration || (imageMapping.operation.includes("error") || imageMapping.operation === "failed" ? 1 : 0)),
    duration: normalizeNumber(duration),
    surfaceKey: safeOperationalText(detail.surfaceKey),
    slotKey: safeOperationalText(detail.slotKey),
    rolloutStage: safeOperationalText(detail.controlStage || detail.rolloutStage, 32),
    rolloutReason: safeOperationalText(detail.controlReason || detail.rolloutReason, 40),
    fallbackDecisionReason: safeOperationalText(detail.fallbackDecisionReason, 40),
    source: safeOperationalText(detail.source, 32),
    migrationState: safeOperationalText(detail.migrationState, 32),
    controlled: Boolean(detail.controlled),
    fallbackUsed: Boolean(detail.fallbackUsed || detail.willFallback),
    legacyFallbackAllowed: detail.legacyFallbackAllowed !== false,
    attempt: normalizeNumber(detail.attempt || detail.attempts, { max: 20, decimals: 0 }),
    queueWaitMs: normalizeNumber(detail.queueWaitMs, { max: 120_000 }),
    decodeDurationMs: normalizeNumber(detail.decodeDurationMs, { max: 120_000 }),
    failureReason: safeOperationalText(detail.failureReason, 40),
  });
  if (accepted) imageEventCounts[imageEventBucket] += 1;
};

const handleRuntimeError = (event) => {
  enqueue("runtime_error", sanitizeErrorForMonitoring(event?.error || event?.message));
};

const handleUnhandledRejection = (event) => {
  enqueue("unhandled_rejection", sanitizeErrorForMonitoring(event?.reason));
};

const handlePageExit = () => {
  flushLongTaskSummary();
  flushPerformanceMonitoring({ beacon: true }).catch(() => null);
};

const handleVisibilityChange = () => {
  if (document.visibilityState === "hidden") handlePageExit();
};

const startLongTaskObserver = () => {
  if (typeof PerformanceObserver === "undefined") return;
  try {
    longTaskObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        longTaskSummary.count += 1;
        longTaskSummary.totalDuration += Number(entry.duration) || 0;
        longTaskSummary.maxDuration = Math.max(longTaskSummary.maxDuration, Number(entry.duration) || 0);
      });
    });
    longTaskObserver.observe({ type: "longtask", buffered: true });
  } catch {
    longTaskObserver = null;
  }
};

export const startProductionPerformanceMonitoring = () => {
  if (initialized || typeof window === "undefined") return config.enabled;
  initialized = true;
  config = resolvePerformanceMonitoringConfig();
  sampled = Math.random() < config.sampleRate;
  currentRoute = sanitizeMonitoringRoute(window.location.pathname);
  updateDebugState(config.enabled ? "ready" : "disabled", "");
  if (!config.enabled) return false;

  window.addEventListener(PORTAL_TELEMETRY_EVENT, handlePortalTelemetry);
  window.addEventListener("error", handleRuntimeError);
  window.addEventListener("unhandledrejection", handleUnhandledRejection);
  window.addEventListener("pagehide", handlePageExit);
  document.addEventListener("visibilitychange", handleVisibilityChange);
  startLongTaskObserver();
  return true;
};

export const __resetPerformanceMonitoringForTests = () => {
  if (flushTimer && typeof window !== "undefined") window.clearTimeout(flushTimer);
  longTaskObserver?.disconnect?.();
  if (typeof window !== "undefined") {
    window.removeEventListener(PORTAL_TELEMETRY_EVENT, handlePortalTelemetry);
    window.removeEventListener("error", handleRuntimeError);
    window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    window.removeEventListener("pagehide", handlePageExit);
  }
  if (typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  }
  config = resolvePerformanceMonitoringConfig();
  initialized = false;
  sampled = false;
  pageViewId = createPageViewId();
  currentRoute = "/";
  queue = [];
  flushTimer = null;
  flushPromise = null;
  navigationTimingRecorded = false;
  longTaskObserver = null;
  longTaskSummary = { count: 0, totalDuration: 0, maxDuration: 0 };
  imageEventCounts = { regular: 0, fallback: 0, failure: 0 };
};

export { MONITORING_EVENT };
